import { Store } from '@ca/common/types'
import { useStore } from 'hooks/useSession'
import { useEffect } from 'react'
import stateStorage from 'storage/stateStorage'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { devtools } from 'utils/devtools'

type LastStoreState = {
  store: Store | null
}

type LastStoreActions = {
  syncStore: (store: Store | undefined) => void
}

/**
 * This is useful for some rare cases
 * where we need to know what was the
 * last valid store that was used to login
 * even if the store is not currently logged in
 *
 */
const useLastStoreState = create<
  LastStoreState & LastStoreActions,
  [
    ['zustand/devtools', { name: 'lastValidStore' }],
    ['zustand/persist', LastStoreState]
  ]
>(
  devtools(
    persist(
      (set) => ({
        store: null,
        syncStore: (store) => set({ store }),
      }),
      {
        name: 'lastValidStore',
        storage: createJSONStorage(() => stateStorage),
        partialize: (state) => ({
          store: state.store,
        }),
      }
    ),
    {
      name: 'lastValidStore',
    }
  )
)

export const useSyncLastValidStore = () => {
  const store = useStore()
  const sync = useLastStoreState((state) => state.syncStore)
  useEffect(() => {
    if (store) {
      // Only sync if the store is set
      sync(store)
    }
  }, [store, sync])
}

export const useLastStore = () => {
  return useLastStoreState((state) => state.store)
}
